import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DifficultySettings = ({ onSave, currentDifficulty }) => {
  const [selectedDifficulty, setSelectedDifficulty] = useState(currentDifficulty || 'beginner');

  const difficultyLevels = [
    {
      id: 'beginner',
      name: 'Beginner',
      icon: 'Sprout',
      color: 'var(--color-success)',
      description: 'Perfect for those new to sales or starting their coaching journey',
      features: [
        'Simple scenarios with clear guidance',
        'Detailed feedback with step-by-step improvements',
        'Longer time to respond to drills',
        'Focus on fundamental sales techniques'
      ]
    },
    {
      id: 'intermediate',
      name: 'Intermediate',
      icon: 'TrendingUp',
      color: 'var(--color-primary)',
      description: 'Ideal for sales reps with some experience looking to refine their skills',
      features: [
        'Moderate complexity scenarios',
        'Balanced feedback with actionable insights',
        'Standard response time expectations',
        'Mix of fundamental and advanced techniques'
      ]
    },
    {
      id: 'advanced',
      name: 'Advanced',
      icon: 'Zap',
      color: 'var(--color-accent)',
      description: 'Designed for experienced sales professionals seeking mastery',
      features: [
        'Complex, multi-layered scenarios',
        'Concise, high-level feedback',
        'Faster response time requirements',
        'Focus on advanced strategies and nuances'
      ]
    }
  ];

  const handleSave = () => {
    onSave({ difficulty: selectedDifficulty });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {difficultyLevels?.map((level) => (
          <button
            key={level?.id}
            onClick={() => setSelectedDifficulty(level?.id)}
            className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
              selectedDifficulty === level?.id
                ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${level?.color}20` }}
              >
                <Icon name={level?.icon} size={24} color={level?.color} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{level?.name}</h3>
                {selectedDifficulty === level?.id && (
                  <span className="text-xs text-primary font-medium">Current Level</span>
                )}
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">{level?.description}</p>
            
            <ul className="space-y-2">
              {level?.features?.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                  <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>
      <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
        <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
        <div className="text-sm text-foreground">
          <p className="font-medium mb-1">Difficulty affects your entire coaching experience</p>
          <p className="text-muted-foreground">
            Changing difficulty will adjust scenario complexity, feedback detail, and progression pacing across all coaching modules including Daily Drills, Objection Dojo, and Script Lab.
          </p>
        </div>
      </div>
      <div className="pt-4">
        <Button 
          variant="default" 
          onClick={handleSave}
          iconName="Save"
          iconPosition="left"
        >
          Save Difficulty Setting
        </Button>
      </div>
    </div>
  );
};

export default DifficultySettings;